import { useEffect, useLayoutEffect, useRef, type ReactElement } from "react";
import css from "./board.module.css";

/**
 * Список операторов ячейки графика (08.09, владелец: «long-press по любой ячейке —
 * дропдаун: у кого зелёные квадратики, а у кого красные»).
 *
 * Показывает всех видимых операторов этой ячейки тремя группами: свободны (зелёный),
 * заняты (красный), не отметили (серый) — независимо от выбранного чипа оператора.
 * Позиция — fixed от точки нажатия с клампом по вьюпорту (сетка скроллится и режет
 * absolute-дропдауны). Закрытие: тап мимо, Esc.
 */
export interface CellOp { id: string; name: string; st: string }

export function SchedCellPop({ at, title, ops, onClose }: {
  at: { x: number; y: number };
  title: string;
  ops: CellOp[];
  onClose: () => void;
}): ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = Math.max(8, Math.min(at.x + 8, window.innerWidth - 8 - r.width));
    const below = at.y + 10;
    const y = below + r.height > window.innerHeight - 8 ? Math.max(8, at.y - 10 - r.height) : below;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  }, [at]);
  useEffect(() => {
    const onDown = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown, true); document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const groups = [
    { k: "free", lbl: "свободны", sq: css.sqFree, list: ops.filter((o) => o.st === "free") },
    { k: "busy", lbl: "заняты", sq: css.sqBusy, list: ops.filter((o) => o.st === "busy") },
    { k: "none", lbl: "не отметили", sq: css.sqNone, list: ops.filter((o) => o.st !== "free" && o.st !== "busy") },
  ];
  const free = groups[0]?.list.length ?? 0;
  return (
    <div ref={ref} className={css.cellPop} role="dialog" aria-label={`Кто свободен: ${title}`}>
      <div className={css.cellPopHead}>{title}<span className={css.cellPopCnt}>свободны {free} из {ops.length}</span></div>
      {ops.length === 0 ? <div className={css.cellPopLbl}>Операторов нет</div> : null}
      {groups.map((g) => (g.list.length ? (
        <div key={g.k} className={css.cellPopGrp}>
          <div className={css.cellPopLbl}><i className={`${css.cellPopSq} ${g.sq}`} aria-hidden="true" />{g.lbl} · {g.list.length}</div>
          <div className={css.cellPopNames}>{g.list.map((o) => <span key={o.id} className={css.cellPopName}>{o.name}</span>)}</div>
        </div>
      ) : null))}
    </div>
  );
}
